import type {
  ExitPlacementDisplay,
  ExitPlacementEngine,
  ExitPlacementSliceRef,
  ManagementExplorerSliceRow,
} from "../types/managementExplorer";
import { formatSegmentKeyDisplay } from "./managementActionLabels";

function humanizeSituationToken(token: string): string {
  const t = token.trim().toLowerCase();
  if (!t) return "";
  if (/^[a-z]{2,5}$/.test(t)) return t.toUpperCase();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** Machine situation string (e.g. `adx=strong`) → `ADX Strong`. */
export function formatSituation(situation: string): string {
  const raw = situation.trim();
  if (!raw) return "";
  return raw
    .split("|")
    .map((part) => {
      const trimmed = part.trim();
      if (!trimmed) return "";
      const eq = trimmed.indexOf("=");
      if (eq < 0) return humanizeSituationToken(trimmed);
      const key = trimmed.slice(0, eq).trim();
      const value = trimmed.slice(eq + 1).trim();
      if (!key && !value) return "";
      if (!value) return humanizeSituationToken(key);
      return `${humanizeSituationToken(key)} ${humanizeSituationToken(value)}`;
    })
    .filter(Boolean)
    .join(" · ");
}

/** Bucket-only label (segment without situation clause). */
export function exitPlacementSliceSegmentLabel(
  row: Pick<ManagementExplorerSliceRow, "segment_label" | "segment_key">
): string {
  return (
    row.segment_label?.trim() ||
    formatSegmentKeyDisplay(row.segment_key ?? "") ||
    ""
  );
}

type ExitPlacementDisplayLabelSource = Pick<
  ManagementExplorerSliceRow,
  "display_label" | "situation" | "segment_label" | "segment_key"
>;

/** Primary UI label for exit-placement slices (API field with legacy fallback). */
export function exitPlacementSliceDisplayLabel(row: ExitPlacementDisplayLabelSource): string {
  const fromApi = row.display_label?.trim();
  if (fromApi) return fromApi;

  const bucket = exitPlacementSliceSegmentLabel(row);
  const situation = row.situation?.trim();
  if (situation) {
    const formatted = formatSituation(situation);
    if (bucket && formatted) return `${bucket} · ${formatted}`;
    return formatted || bucket;
  }
  return bucket;
}

/** Default sim/backtest title from simulate-exit-placement `exit_placement_slices`. */
export function exitPlacementSlicesDefaultTitle(
  slices: ExitPlacementSliceRef[] | null | undefined
): string | null {
  if (!slices?.length) return null;
  const labels = slices
    .map((slice) => exitPlacementSliceDisplayLabel(slice))
    .filter((label) => Boolean(label.trim()));
  if (!labels.length) return null;
  if (labels.length === 1) return labels[0];
  return labels.join(" + ");
}

export function sliceExitPlacement(row: ManagementExplorerSliceRow) {
  return row.exit_placement;
}

export function sliceExitDisplay(row: ManagementExplorerSliceRow): ExitPlacementDisplay | null | undefined {
  return row.exit_placement?.display;
}

export function formatPriceDistanceFromEntry(
  value: number | null | undefined,
  symbol?: string | null
): string {
  if (value == null || !Number.isFinite(value)) return "—";
  const sym = String(symbol ?? "").trim();
  const abs = Math.abs(value);
  const formatted = abs >= 100 ? abs.toFixed(0) : abs.toFixed(2);
  const prefix = sym === "XAUUSD" || sym.endsWith("USD") ? "$" : "";
  return `${prefix}${formatted} from entry`;
}

export function formatVolMultiple(value: number | null | undefined): string | null {
  if (value == null || !Number.isFinite(value)) return null;
  return `${value.toFixed(2)}× vol`;
}

export function formatRewardRisk(engine?: ExitPlacementEngine | null): string {
  const rr = engine?.reward_risk;
  if (rr == null || !Number.isFinite(rr)) return "—";
  return `${rr.toFixed(1)}:1`;
}

export function exitPlacementStopTargetTooltip(row: ManagementExplorerSliceRow): string | undefined {
  const display = sliceExitDisplay(row);
  if (!display) return undefined;
  const parts: string[] = [];
  const slVol = formatVolMultiple(display.sl_vol_multiple);
  const tpVol = formatVolMultiple(display.tp_vol_multiple);
  if (slVol) parts.push(`Stop: ${slVol}`);
  if (tpVol) parts.push(`Target: ${tpVol}`);
  const atrLabel = display.reference_atr_label?.trim();
  const atrPrice = display.reference_atr_price;
  if (atrLabel) {
    parts.push(
      atrPrice != null && Number.isFinite(atrPrice)
        ? `${atrLabel} ($${atrPrice.toFixed(2)})`
        : atrLabel
    );
  }
  return parts.length ? parts.join(" · ") : undefined;
}

export function levelSourceCaveat(levelSource?: string | null): string | null {
  if (String(levelSource ?? "").trim() === "price_movement") {
    return "Distances inferred from MAE/MFE — not your broker SL/TP levels.";
  }
  return null;
}

export function sliceHasActionableExit(row: ManagementExplorerSliceRow): boolean {
  if (typeof row.has_actionable_exit === "boolean") return row.has_actionable_exit;
  if (typeof row.has_actionable_management === "boolean") return row.has_actionable_management;
  return Boolean(row.simulation_rule);
}

export function sliceHasStopOrTargetPrice(row: ManagementExplorerSliceRow): boolean {
  const display = sliceExitDisplay(row);
  return (
    (display?.sl_distance_price != null && Number.isFinite(display.sl_distance_price)) ||
    (display?.tp_distance_price != null && Number.isFinite(display.tp_distance_price))
  );
}

/** Info copy when a slice has no actionable SL/TP pair (not an error state). */
export function sliceNoActionMessage(row: ManagementExplorerSliceRow): string {
  const exit = sliceExitPlacement(row);
  const reason = String(exit?.no_action_reason ?? "").trim();
  if (reason) return reason;
  if (!sliceHasStopOrTargetPrice(row)) {
    return String(exit?.display?.plain_summary ?? "").trim();
  }
  return "";
}

export function sliceCanAddToCart(row: ManagementExplorerSliceRow): boolean {
  return sliceHasActionableExit(row) && Boolean(row.simulation_rule);
}

export const EXIT_PLACEMENT_VOL_GLOSSARY =
  "Volatility unit = ATR at entry on your report timeframe. Recalculate at entry using current ATR.";
